import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fields = [
  ['date', 'TRIPLOG.SERVICECOSTS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['serviceType', 'TRIPLOG.SERVICECOSTS.TABLE.serviceType'],
  ['kilometers', 'TRIPLOG.SERVICECOSTS.TABLE.kilometers'],
  ['laborCost', 'TRIPLOG.SERVICECOSTS.TABLE.laborCost'],
  ['materialCost', 'TRIPLOG.SERVICECOSTS.TABLE.materialCost'],
  ['totalCost', 'TRIPLOG.SERVICECOSTS.TABLE.totalCost'],
  ['description', 'TRIPLOG.SERVICECOSTS.TABLE.description'],
  ['workshopName', 'TRIPLOG.SERVICECOSTS.TABLE.workshopName'],
  ['workshopAddress', 'TRIPLOG.SERVICECOSTS.TABLE.workshopAddress'],
  ['documentNumber', 'TRIPLOG.SERVICECOSTS.TABLE.documentNumber'],
  ['note', 'TRIPLOG.SERVICECOSTS.TABLE.note'],
]

const NUMERIC_FIELDS = [
  'kilometers',
  'laborCost',
  'materialCost',
  'totalCost'
]

const Row = ({ label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label style={{ width: layout.labelW, fontWeight: 700, fontSize: layout.labelFont }}>
      {label}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', width: layout.inputW }}>
      {children}
    </div>
  </div>
)

const REQUIRED_FIELDS = ['date', 'vehicleId']

const ServiceAddModal = ({ open, onClose, onSaved, userId }) => {
  const { t } = useTranslation()

  const [vw, setVw] = useState(window.innerWidth)
  const [form, setForm] = useState({})
  const [vehiclesById, setVehiclesById] = useState({})
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  let layout = {}

  if (vw >= 1920) {
    layout = { labelW: 250, inputW: 350, inputH: 20, labelFont: 15, inputFont: 15, rowGap: 4 }
  } else if (vw >= 1366) {
    layout = { labelW: 220, inputW: 340, inputH: 18, labelFont: 14, inputFont: 14, rowGap: 3 }
  } else {
    layout = { labelW: 200, inputW: 240, inputH: 18, labelFont: 13, inputFont: 13, rowGap: 3 }
  }

  const inputStyle = {
    height: layout.inputH,
    fontSize: layout.inputFont,
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #999',
    width: '100%'
  }

  const modalMaxWidth =
    vw >= 2600 ? '18vw' :  vw >= 2400 ? '23vw' : vw >= 1920 ? '29vw' : vw >= 1366 ? '35vw' : '35vw'

  const modalTitleFontSize =
    vw >= 1920 ? 24 :
    vw >= 1366 ? 24 :
    18

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const loadVehicles = useCallback(async () => {
    if (!userId) return

    try {
      const res = await fetchWithAuth(`/users/${userId}/vehicles`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      const index = data.reduce((acc, vehicle) => {
        if (vehicle?.id == null) return acc
        acc[String(vehicle.id)] = vehicle
        return acc
      }, {})

      setVehiclesById(index)
    } catch {}
  }, [userId])

  useEffect(() => {
    if (!open) return
    setForm({})
    setErrors({})
    setMessage(null)
    loadVehicles()
  }, [open, loadVehicles])

useEffect(() => {
  const vehicles = Object.values(vehiclesById)

  if (vehicles.length !== 1) return
  if (form.vehicleId) return

  const vehicle = vehicles[0]

  setForm(prev => ({
    ...prev,
    vehicleId: String(vehicle.id)
  }))
}, [vehiclesById, form.vehicleId])

useEffect(() => {
  const labor = parseFloat(form.laborCost)
  const material = parseFloat(form.materialCost)

  const laborVal = isNaN(labor) ? 0 : labor
  const materialVal = isNaN(material) ? 0 : material

const total = laborVal + materialVal

setForm(prev => ({
  ...prev,
  totalCost: total.toFixed(2)
}))

}, [form.laborCost, form.materialCost])

const validate = () => {
  const newErrors = {}

  REQUIRED_FIELDS.forEach(field => {
    if (!form[field] || String(form[field]).trim() === '') {
      newErrors[field] = true
    }
  })

  if (!form.totalCost || String(form.totalCost).trim() === '') {
    newErrors.totalCost = true
  }

  setErrors(newErrors)

  if (Object.keys(newErrors).length > 0) {
    setMessage({ message: t('TRIPLOG.FORM.REQUIRED_FIELDS'), type: 'error' })
    return false
  }

  return true
}

const formatNumber = (val) => {
  if (val === '' || val == null) return null
  return Number(parseFloat(val).toFixed(2))
}

const saveService = async () => {
  try {

    if (!validate()) {
      return
    }

    setLoading(true)

    const res = await fetchWithAuth('/triplog/service-costs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
  ...form,
  kilometers: formatNumber(form.kilometers),
  laborCost: formatNumber(form.laborCost),
  materialCost: formatNumber(form.materialCost),
  totalCost: formatNumber(form.totalCost)
})
      })

      if (!res.ok) {
        setMessage({ message: t('vehicle_save_failed'), type: 'error' })
        return
      }

      const created = await res.json().catch(() => form)

      setMessage({ message: t('SAVED'), type: 'success' })
      onSaved?.(created)
      onClose()

    } catch {
      setMessage({ message: t('CONNECTION_ERROR'), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <>
      <Overlay open={loading} zIndex={4000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '5px 30px 10px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth: modalMaxWidth,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <h3
            style={{
              fontSize: modalTitleFontSize,
              marginTop: 5,
              marginBottom: 20
            }}
          >
            {t('TRIPLOG.SERVICECOSTS.TITLE')}
          </h3>

          {message && <MessageBox message={message.message} type={message.type} />}

          <div style={{ display: 'flex', flexDirection: 'column', gap: layout.rowGap, overflowY: 'auto', flex: 1 }}>
            {fields.map(([key, labelKey]) => {

              if (key === 'vehicleId') {
                return (
                  <Row key={key} label={t(labelKey)} layout={layout}>
<select
  id={key}
  value={form.vehicleId ?? ''}
  onChange={e => {
    setForm(prev => ({
      ...prev,
      vehicleId: e.target.value || undefined
    }))
    setErrors(prev => ({ ...prev, vehicleId: false }))
  }}
  style={{
    ...inputStyle,
    height: 34,
    border: errors.vehicleId ? '1px solid #c62828' : inputStyle.border,
    background: errors.vehicleId ? 'rgba(255,0,0,0.1)' : '#fff'
  }}
>
  <option value="">--</option>
  {Object.values(vehiclesById).map(v => (
    <option key={v.id} value={v.id}>
      {`${v.name || v.model || '-'} (${v.licensePlate || v.spz || '-'})`}
    </option>
  ))}
</select>
                  </Row>
                )
              }

              if (key === 'spz') {
                const vehicle = vehiclesById[String(form.vehicleId)]

                return (
                  <Row key={key} label={t(labelKey)} layout={layout}>
                    <input
                      value={vehicle?.licensePlate || vehicle?.spz || ''}
                      disabled
                      style={{ ...inputStyle, background: '#ddd' }}
                    />
                  </Row>
                )
              }

if (key === 'totalCost') {
  const shouldAutoCalculate = form.laborCost || form.materialCost

  return (
    <Row key={key} label={t(labelKey)} layout={layout}>
      <input
        value={form.totalCost || ''}
        disabled={shouldAutoCalculate}
onChange={e => {
  let val = e.target.value

  val = val.replace(',', '.')
  if (!/^\d*\.?\d{0,2}$/.test(val)) {
    return
  }

  setForm(prev => ({ ...prev, totalCost: val }))
  setErrors(prev => ({ ...prev, totalCost: false }))
}}
        style={{
          ...inputStyle,
          background: shouldAutoCalculate ? '#ddd' : (errors.totalCost ? 'rgba(255,0,0,0.1)' : '#fff'),
          border: errors.totalCost ? '1px solid #c62828' : inputStyle.border
        }}
      />
    </Row>
  )
}

              const isDate = key === 'date'
              const isTextarea = key === 'note' || key === 'description'

              return (
                <Row key={key} label={t(labelKey)} layout={layout}>
                  {isTextarea ? (
                    <textarea
                      value={form[key] || ''}
                      onChange={e => {
                        setForm(prev => ({ ...prev, [key]: e.target.value }))
                        setErrors(prev => ({ ...prev, [key]: false }))
                      }}
                      style={{ ...inputStyle, resize: 'none' }}
                    />
                  ) : (
                    <input
                      type={isDate ? 'date' : 'text'}
                      value={form[key] || ''}
onChange={e => {
  let val = e.target.value

  if (NUMERIC_FIELDS.includes(key)) {
    val = val.replace(',', '.')
    if (!/^\d*\.?\d{0,2}$/.test(val)) {
      return
    }
  }

  setForm(prev => ({ ...prev, [key]: val }))
  setErrors(prev => ({ ...prev, [key]: false }))
}}
                      onClick={e => {
                        if (isDate && e.target.showPicker) {
                          e.target.showPicker()
                        }
                      }}
                      style={{
                        ...inputStyle,
                        background: errors[key] ? 'rgba(255,0,0,0.1)' : '#fff',
                        border: errors[key] ? '1px solid #c62828' : inputStyle.border
                      }}
                    />
                  )}
                </Row>
              )
            })}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 15, justifyContent: 'center' }}>
            <button
              style={modalPrimaryButtonStyle(pressedBtn, 'saveService', hoverBtn === 'saveService')}
              onMouseEnter={() => setHoverBtn('saveService')}
              onMouseLeave={() => { setHoverBtn(null); setPressedBtn(null) }}
              onMouseDown={() => setPressedBtn('saveService')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={saveService}
            >
              {t('SAVE')}
            </button>

            <button
              style={modalSecondaryButtonStyle(pressedBtn, 'cancelService', hoverBtn === 'cancelService')}
              onMouseEnter={() => setHoverBtn('cancelService')}
              onMouseLeave={() => { setHoverBtn(null); setPressedBtn(null) }}
              onMouseDown={() => setPressedBtn('cancelService')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CANCEL')}
            </button>
          </div>

        </div>
      </div>
    </>
  )
}

export default ServiceAddModal